/**
 * EventStream Component
 *
 * Displays execution events in a flat, compact format:
 * - Event types: action (processing), action_success, action_failed, final_response
 * - Shows 'purpose' field as the action content
 * - Collapsible 240px window that scrolls to bottom
 * - Expandable to full height
 */

import React, { useRef, useEffect, useState } from 'react';
import { ParsedEvent } from '../../types/session';

interface EventStreamProps {
  events: ParsedEvent[];
  startTime?: string;
  endTime?: string;
  autoScroll?: boolean;
  alwaysExpanded?: boolean; // For job view - no toggle
  agentSpecId?: string; // Agent spec ID for "Agent Specs" link
  sessionId?: string; // Session ID for fetching logs
  onStepSelect?: (functionCallId: string) => void; // Callback when clicking an individual step
  selectedFunctionCallId?: string | null; // Currently selected step (highlighted in blue)
  waitingForConfirmation?: boolean; // Step mode: agent is paused
  pendingToolName?: string; // Step mode: name of the pending tool
  isStepMode?: boolean; // Whether session is in step mode
  onLearningsClick?: (learnings: string[]) => void; // Callback when clicking learnings link
}

interface ProcessedEvent {
  id: string;
  title: string;
  isProcessing: boolean;
  hasError?: boolean;
  originalIndex: number;
  functionCallId?: string; // For fetching step-specific logs
  duration?: number; // Duration in seconds
  thoughtDuration?: number; // AI thought duration before this tool call
  planningText?: string; // Model's explicit reasoning text before this action
  tokenUsage?: { input: number; output: number; thinking: number; cached: number }; // Per-step LLM token counts
  isFirst?: boolean; // First action (suppress server-side thought duration, use startup profile instead)
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
}

// Hardcoded model rates ($ per 1M tokens)
// Gemini 2.5 Flash / Supernova: input $0.30, output+thinking $2.50
// Cerebras OSS: input $0.35, output $0.75
const MODEL_RATES = {
  gemini: { input: 0.30, output: 2.50 },   // thinking billed at output rate
  cerebras: { input: 0.35, output: 0.75 },
};

function calcStepCost(usage: { input: number; output: number; thinking: number; cached: number }): number {
  const rates = MODEL_RATES.gemini; // default to gemini for now
  return (usage.input * rates.input + (usage.output + usage.thinking) * rates.output) / 1_000_000;
}

function formatCost(dollars: number): string {
  if (dollars < 0.001) return `$${(dollars * 1000).toFixed(2)}m`;  // millicents for tiny amounts
  if (dollars < 0.01) return `$${dollars.toFixed(4)}`;
  if (dollars < 1) return `$${dollars.toFixed(3)}`;
  return `$${dollars.toFixed(2)}`;
}

/** Inline accordion for planning text + thought duration + token usage. */
const PlanningAccordion: React.FC<{
  planningText?: string;
  thoughtDuration?: number;
  tokenUsage?: { input: number; output: number; thinking: number; cached: number };
  eventId: string;
  expandedIds: Set<string>;
  toggleExpanded: (id: string) => void;
}> = ({ planningText, thoughtDuration, tokenUsage, eventId, expandedIds, toggleExpanded }) => {
  if (!planningText && thoughtDuration == null) return null;

  // Build metadata suffix: "(0.25s · 1.2k→0.3k · $0.003)" or "(0.25s)" or "(1.2k→0.3k)"
  const hasDuration = thoughtDuration != null;
  const hasTokens = tokenUsage && (tokenUsage.input > 0 || tokenUsage.output > 0);
  const stepCost = hasTokens ? calcStepCost(tokenUsage!) : 0;
  const metaParts: string[] = [];
  if (hasDuration) metaParts.push(`${thoughtDuration.toFixed(2)}s`);
  if (hasTokens) metaParts.push(`${formatTokens(tokenUsage!.input)}→${formatTokens(tokenUsage!.output)}`);
  if (stepCost > 0) metaParts.push(formatCost(stepCost));
  const metaSuffix = metaParts.length > 0 ? ` (${metaParts.join(' · ')})` : '';

  // Fallback: no planning text, just show old "thought for Xs" with tokens
  if (!planningText) {
    const costStr = stepCost > 0 ? ` · ${formatCost(stepCost)}` : '';
    return (
      <div className="text-sm flex gap-2">
        <div className="flex-shrink-0 w-4"></div>
        <div className="text-gray-400 italic">
          thought for {thoughtDuration!.toFixed(2)}s{hasTokens ? ` · ${formatTokens(tokenUsage!.input)}→${formatTokens(tokenUsage!.output)}${costStr}` : ''}
        </div>
      </div>
    );
  }

  const lines = planningText.split('\n').filter(l => l.trim());
  const firstLine = lines[0] || '';
  const hasMultipleLines = lines.length > 1;
  const isExpanded = expandedIds.has(eventId);

  return (
    <div className="text-sm flex gap-2">
      <div className="flex-shrink-0 w-4"></div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-gray-400 italic ${hasMultipleLines ? 'cursor-pointer hover:text-gray-500' : ''}`}
          onClick={hasMultipleLines ? () => toggleExpanded(eventId) : undefined}
        >
          {hasMultipleLines && (
            <svg
              className={`inline w-3 h-3 mr-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
          {isExpanded ? (
            <span className="whitespace-pre-wrap">{planningText}{metaSuffix}</span>
          ) : (
            <span>{firstLine}{metaSuffix}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export const EventStream: React.FC<EventStreamProps> = ({
  events,
  startTime,
  endTime,
  autoScroll = true,
  alwaysExpanded = false,
  agentSpecId,
  sessionId,
  onStepSelect,
  selectedFunctionCallId,
  waitingForConfirmation = false,
  pendingToolName,
  isStepMode = false,
  onLearningsClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [thinkingSeconds, setThinkingSeconds] = useState(0);
  const [expandedPlanningIds, setExpandedPlanningIds] = useState<Set<string>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);

  const togglePlanningExpanded = React.useCallback((id: string) => {
    setExpandedPlanningIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Count actions (steps) - only count 'action' events (action_success/action_failed are status updates)
  const stepCount = events.filter(e => e.type === 'action').length;

  // Build spec link - use prop if provided, otherwise extract from events
  const membankUrl = process.env.REACT_APP_MEMBANK_URL || 'http://localhost:8002';
  const specLink = React.useMemo(() => {
    // First try the prop (from SSE or session object)
    if (agentSpecId) {
      return `${membankUrl}/specs/${agentSpecId}`;
    }
    // Fallback: extract from initialize_event in events array
    const initEvent = events.find(e => e.type === 'initialize_event');
    const initData = initEvent?.data?.data || initEvent?.data;
    const specId = initData?.agent_spec_id;
    return specId ? `${membankUrl}/specs/${specId}` : null;
  }, [agentSpecId, events, membankUrl]);

  // Auto-scroll to bottom when new events arrive (only if autoScroll is true)
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events.length, autoScroll]);

  // Timer - count with 10ms precision
  useEffect(() => {
    if (!endTime) {
      // Session running - tick from 0
      const t0 = Date.now();
      setSeconds(0);
      const interval = setInterval(() => {
        setSeconds((Date.now() - t0) / 1000);
      }, 10);
      return () => clearInterval(interval);
    } else if (startTime && endTime) {
      // Session completed - calculate final duration
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      setSeconds(Math.max(0, (end - start) / 1000));
    }
  }, [endTime, startTime]);

  // Format duration
  const formatDuration = (secs: number): string => {
    if (secs < 60) return `${secs.toFixed(2)}s`;
    const minutes = Math.floor(secs / 60);
    const remainingSeconds = secs % 60;
    return `${minutes}m ${remainingSeconds.toFixed(2)}s`;
  };

  // Determine if we should show "thinking..." indicator
  // Show when: session running, no final_response, and either:
  //   - No actions yet (waiting for first action), or
  //   - All actions are completed (waiting for next action)
  const hasFinalResponse = events.some(e => e.type === 'final_response');
  const actionEvents = events.filter(e => e.type === 'action');
  const completedActionCount = events.filter(e => e.type === 'action_success' || e.type === 'action_failed').length;
  const allActionsCompleted = actionEvents.length === 0 || completedActionCount >= actionEvents.length;
  const isWaitingForFirstEvent = !endTime && events.length === 0 && !!startTime;
  const isThinking = !endTime && events.length > 0 && allActionsCompleted && !hasFinalResponse;
  const shouldTickTimer = isWaitingForFirstEvent || isThinking;

  // Thinking timer - increments by 0.01s (display only, actual duration comes from server)
  useEffect(() => {
    if (shouldTickTimer) {
      setThinkingSeconds(0);
      const t0 = Date.now();
      const interval = setInterval(() => {
        setThinkingSeconds((Date.now() - t0) / 1000);
      }, 10);
      return () => clearInterval(interval);
    } else {
      setThinkingSeconds(0);
    }
  }, [shouldTickTimer, completedActionCount]);

  // Process events into display format
  // New event format: action, action_success, action_failed, final_response
  // Track action completion by matching action -> action_success/action_failed sequence
  const processedEvents: ProcessedEvent[] = [];

  // Count how many actions have been completed (success or failed)
  let completedCount = 0;
  let failedIndices = new Set<number>();
  // Map function_call_id to duration
  const durationMap = new Map<string, number>();

  // First pass: count completed actions, track which ones failed, and extract durations
  let totalActionCount = 0;
  let hasErrorEvent = false;
  for (const event of events) {
    if (event.type === 'action') {
      totalActionCount++;
    } else if (event.type === 'action_success') {
      completedCount++;
      // Store duration if available
      if (event.function_call_id && event.duration != null) {
        durationMap.set(event.function_call_id, event.duration);
      }
    } else if (event.type === 'action_failed') {
      completedCount++;
      failedIndices.add(completedCount); // Mark this action index as failed
      // Store duration if available
      if (event.function_call_id && event.duration != null) {
        durationMap.set(event.function_call_id, event.duration);
      }
    } else if (event.type === 'error') {
      hasErrorEvent = true;
    }
  }

  // If there's an error event and there are incomplete actions, mark them as failed
  if (hasErrorEvent && totalActionCount > completedCount) {
    // Mark all incomplete actions as failed
    for (let i = completedCount + 1; i <= totalActionCount; i++) {
      failedIndices.add(i);
    }
    completedCount = totalActionCount; // All actions are now "completed" (as failed)
  }

  // If session has ended (endTime set) and no action_success events, mark all as completed
  // This handles MCP sessions which only have action events without action_success
  if (endTime && completedCount === 0 && totalActionCount > 0) {
    completedCount = totalActionCount;
  }

  // Extract startup profile from initialize_event
  const startupProfile = React.useMemo(() => {
    const initEvent = events.find(e => e.type === 'initialize_event');
    if (!initEvent) return null;
    // Try all possible data paths (depends on how SSE parser nests it)
    return initEvent.data?.startup_profile
      || initEvent.data?.data?.startup_profile
      || (initEvent.data?.initialize_event as any)?.startup_profile
      || null;
  }, [events]);

  // Extract learnings from initialize_event
  const learnings: string[] = React.useMemo(() => {
    const initEvent = events.find(e => e.type === 'initialize_event');
    if (!initEvent) return [];
    return initEvent.data?.learnings
      || initEvent.data?.data?.learnings
      || [];
  }, [events]);

  // Second pass: process events for display
  let displayActionIndex = 0;
  for (let i = 0; i < events.length; i++) {
    const event = events[i];

    // Skip initialize_event - startup timing rendered separately
    if (event.type === 'initialize_event') {
      continue;
    }

    // Skip action_success and action_failed - they update the action event status
    if (event.type === 'action_success' || event.type === 'action_failed') {
      continue;
    }

    // Handle action events
    if (event.type === 'action') {
      displayActionIndex++;
      const isFirst = displayActionIndex === 1;
      const isCompleted = displayActionIndex <= completedCount;
      const hasFailed = failedIndices.has(displayActionIndex);
      // Get duration from server map, thought_duration from action event
      const duration = event.function_call_id ? durationMap.get(event.function_call_id) : undefined;

      processedEvents.push({
        id: event.id || `event-${i}`,
        title: event.purpose || 'Processing...',
        isProcessing: !isCompleted,
        hasError: hasFailed,
        originalIndex: i,
        functionCallId: event.function_call_id,
        duration,
        thoughtDuration: isFirst ? undefined : event.thought_duration, // First action uses startup profile instead
        planningText: isFirst ? undefined : event.planning_text,
        tokenUsage: event.token_usage,
        isFirst,
      });
    }
    // Handle final_response (session end)
    else if (event.type === 'final_response') {
      // final_response marks the end of the session
      // If error_message is set, session failed
      if (event.error_message) {
        processedEvents.push({
          id: event.id || `event-${i}`,
          title: event.error_message,
          isProcessing: false,
          hasError: true,
          originalIndex: i,
        });
      }
      // Don't show final_response as an event if successful - it's shown in output panel
    }
    // Skip error events - they mark the last action as failed, not displayed separately
    // else if (event.type === 'error') { ... }
  }

  // Calculate final thought duration: time between last action completing and final response
  // The LLM reads the last tool result, decides it's done, and generates the text answer.
  // This is the last "thought for" — captured as the thought_duration on the final_response event
  // from the server, or estimated from timestamps.
  const finalThoughtDuration = React.useMemo(() => {
    if (!endTime) return undefined;
    const fr = events.find(e => e.type === 'final_response');

    // Use thought_duration from final_response if available (set by SSE parser)
    if (fr?.thought_duration && fr.thought_duration > 0.05) {
      return fr.thought_duration;
    }

    // Fall back: time between last action_success and final_response
    const results = events.filter(e => e.type === 'action_success' || e.type === 'action_failed');
    if (results.length === 0 || !fr?.timestamp) return undefined;
    const lastResult = results[results.length - 1];
    if (!lastResult?.timestamp) return undefined;

    const frTs = typeof fr.timestamp === 'number' ? fr.timestamp : 0;
    const lastTs = typeof lastResult.timestamp === 'number' ? lastResult.timestamp : 0;
    if (frTs > lastTs && (frTs - lastTs) > 0.1) {
      return Math.round((frTs - lastTs) * 100) / 100;
    }
    return undefined;
  }, [events, endTime]);

  // Aggregate timing: browser time = browser_init + action durations
  const totalBrowserTime = React.useMemo(() => {
    let total = 0;
    if (startupProfile?.browser_init_ms) {
      total += startupProfile.browser_init_ms / 1000;
    }
    for (const event of events) {
      if ((event.type === 'action_success' || event.type === 'action_failed') && event.duration != null) {
        total += event.duration;
      }
    }
    return total;
  }, [events, startupProfile]);

  // Aggregate timing: LLM time = first_llm + thought durations + final thought
  const totalLlmTime = React.useMemo(() => {
    let total = 0;
    if (startupProfile?.first_llm_ms) {
      total += startupProfile.first_llm_ms / 1000;
    }
    // thought_duration on action events (except first, which uses startup profile)
    let actionIdx = 0;
    for (const event of events) {
      if (event.type === 'action') {
        actionIdx++;
        if (actionIdx > 1 && event.thought_duration != null) {
          total += event.thought_duration;
        }
      }
    }
    if (finalThoughtDuration != null) {
      total += finalThoughtDuration;
    }
    return total;
  }, [events, startupProfile, finalThoughtDuration]);

  // Aggregate token usage and cost across all events
  const totalTokens = React.useMemo(() => {
    let input = 0, output = 0, thinking = 0;
    for (const event of events) {
      if (event.token_usage) {
        input += event.token_usage.input;
        output += event.token_usage.output;
        thinking += event.token_usage.thinking;
      }
    }
    if (input === 0 && output === 0) return null;
    const cost = calcStepCost({ input, output, thinking, cached: 0 });
    return { input, output, cost };
  }, [events]);

  // Don't show header if no events
  const hasEvents = events.length > 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header - "Worked for duration" + step count */}
      {hasEvents && (
        alwaysExpanded ? (
          // Non-clickable header for job view
          <div className="flex-shrink-0 px-4 py-2 text-xs font-medium text-gray-700 flex items-center justify-between">
            <span>
              {isStepMode ? (
                <>Step {stepCount}</>
              ) : (
                <>
                  Worked for {formatDuration(seconds)}
                  {(totalBrowserTime > 0 || totalLlmTime > 0) && (
                    <span className="text-gray-400 ml-1">
                      — browser: {formatDuration(totalBrowserTime)}, LLM: {formatDuration(totalLlmTime + (shouldTickTimer ? thinkingSeconds : 0))}
                      {totalTokens && <>, {formatTokens(totalTokens.input)}→{formatTokens(totalTokens.output)} tokens, {formatCost(totalTokens.cost)}</>}
                    </span>
                  )}
                </>
              )}
            </span>
            <div className="flex items-center gap-3">
              {specLink && (
                <a
                  href={specLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Agent Specs
                </a>
              )}
              <span className="text-gray-500">{stepCount} {stepCount === 1 ? 'step' : 'steps'}</span>
            </div>
          </div>
        ) : (
          // Clickable toggle for playground view
          <div className="flex-shrink-0 px-4 py-2 text-xs font-medium text-gray-700 flex items-center justify-between">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-left hover:bg-gray-50 flex items-center gap-1"
            >
              <span>
                {isStepMode ? (
                  <>Step {stepCount}</>
                ) : (
                  <>
                    Worked for {formatDuration(seconds)}
                    {(totalBrowserTime > 0 || totalLlmTime > 0) && (
                      <span className="text-gray-400 ml-1">
                        — browser: {formatDuration(totalBrowserTime)}, LLM: {formatDuration(totalLlmTime + (shouldTickTimer ? thinkingSeconds : 0))}
                        {totalTokens && <>, {formatTokens(totalTokens.input)}→{formatTokens(totalTokens.output)} tokens, {formatCost(totalTokens.cost)}</>}
                      </span>
                    )}
                  </>
                )}
              </span>
              <svg
                className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="flex items-center gap-3">
              {specLink && (
                <a
                  href={specLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Agent Specs
                </a>
              )}
              <span className="text-gray-500">{stepCount} {stepCount === 1 ? 'step' : 'steps'}</span>
            </div>
          </div>
        )
      )}

      {/* Event List */}
      <div
        className={`overflow-y-auto ${alwaysExpanded || isExpanded ? 'flex-1' : 'h-60'} transition-all min-h-0`}
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="space-y-3 p-4">
          {processedEvents.length === 0 && !startupProfile ? (
            /* Before first event: show live thinking timer */
            isWaitingForFirstEvent ? (
              <div className="text-sm flex gap-2">
                <div className="flex-shrink-0 w-4"></div>
                <div className="text-gray-400 italic animate-pulse">
                  thinking for {thinkingSeconds.toFixed(2)}s
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No events yet...</p>
            )
          ) : (
            <>
              {/* Startup profile: browser Xs, thought for Xs */}
              {startupProfile && (
                <>
                  {(startupProfile.browser_init_ms ?? 0) > 0 && (
                    <div className="text-sm flex gap-2">
                      <div className="flex-shrink-0 w-4"></div>
                      <div className="text-gray-400 italic">
                        browser {(startupProfile.browser_init_ms / 1000).toFixed(1)}s
                        {startupProfile.proxy_country_code && (
                          <span className="ml-2 text-blue-400">
                            proxy: {startupProfile.proxy_country_code.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {(startupProfile.first_llm_ms ?? 0) > 100 && (
                    <div className="text-sm flex gap-2">
                      <div className="flex-shrink-0 w-4"></div>
                      <div className="text-gray-400 italic">
                        thought for {(startupProfile.first_llm_ms / 1000).toFixed(2)}s
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Learnings link - shown before first action when learnings exist */}
              {learnings.length > 0 && (
                <div className="text-sm flex gap-2">
                  <div className="flex-shrink-0 w-4"></div>
                  <button
                    className="text-purple-600 hover:text-purple-800 hover:underline italic"
                    onClick={() => onLearningsClick?.(learnings)}
                  >
                    {learnings.length} {learnings.length === 1 ? 'learning' : 'learnings'} from past sessions
                  </button>
                </div>
              )}
              {processedEvents.map((event, index) => {
                const isClickable = sessionId && onStepSelect && event.functionCallId && !event.isProcessing;
                const isSelected = event.functionCallId != null && event.functionCallId === selectedFunctionCallId;
                const handleClick = () => {
                  if (isClickable) {
                    onStepSelect(event.functionCallId!);
                  }
                };

                return (
                  <React.Fragment key={event.id}>
                    {/* Planning text / thought duration - indented to align with content */}
                    {(event.planningText || event.thoughtDuration != null) && (
                      <PlanningAccordion
                        planningText={event.planningText}
                        thoughtDuration={event.thoughtDuration}
                        tokenUsage={event.tokenUsage}
                        eventId={event.id}
                        expandedIds={expandedPlanningIds}
                        toggleExpanded={togglePlanningExpanded}
                      />
                    )}
                    <div
                      className={`text-sm flex gap-2 ${isClickable ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded' : ''} ${isSelected ? 'bg-blue-50 -mx-2 px-2 py-1 rounded' : ''}`}
                      onClick={handleClick}
                    >
                      {/* Status indicator - fixed width column */}
                      <div className="flex-shrink-0 w-4">
                        {event.isProcessing ? (
                          <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse mt-1.5"></span>
                        ) : event.hasError ? (
                          <span className="text-red-500 text-base">✕</span>
                        ) : isSelected ? (
                          <span className="text-blue-600 text-base">✓</span>
                        ) : (
                          <span className="text-green-500 text-base">✓</span>
                        )}
                      </div>

                      {/* Title - aligned column */}
                      <div className="flex-1 min-w-0">
                        <div className={`${isSelected ? 'text-blue-600 font-medium' : event.hasError ? 'text-red-600' : 'text-gray-900'} break-all ${event.isProcessing ? 'animate-pulse' : ''} ${isClickable && !isSelected ? 'hover:text-blue-600' : ''}`}>
                          {event.duration != null && (
                            <span className={`${isSelected ? 'text-blue-400' : 'text-gray-400'} mr-1`}>({event.duration.toFixed(1)}s)</span>
                          )}
                          {event.title}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {/* Step mode: paused indicator */}
              {waitingForConfirmation && (
                <div className="text-sm flex gap-2">
                  <div className="flex-shrink-0 w-4">
                    <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5"></span>
                  </div>
                  <div className="text-amber-600 font-medium">
                    paused before {pendingToolName || 'next tool'}
                    <span className="text-amber-400 font-normal ml-1">— click Next to continue</span>
                  </div>
                </div>
              )}

              {/* Live thinking indicator - indented to align with content */}
              {isThinking && !waitingForConfirmation && (
                <div className="text-sm flex gap-2">
                  <div className="flex-shrink-0 w-4"></div>
                  <div className="text-gray-400 italic animate-pulse">
                    thinking for {thinkingSeconds.toFixed(2)}s
                  </div>
                </div>
              )}

              {/* Final thought duration before session end */}
              {endTime && finalThoughtDuration != null && finalThoughtDuration > 0.1 && (
                <div className="text-sm flex gap-2">
                  <div className="flex-shrink-0 w-4"></div>
                  <div className="text-gray-400 italic">
                    thought for {finalThoughtDuration.toFixed(2)}s
                  </div>
                </div>
              )}

              {/* Session completed message */}
              {endTime && (
                <div className="text-sm text-gray-500 italic pt-2">
                  Session completed
                </div>
              )}
            </>
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
};
